import { Component } from "react";
import PropTypes from "prop-types";
// @mui
import { Box, Button, Stack, Typography } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";
// utils
import logger from "src/utils/logger";
import * as Sentry from "@sentry/react";

// ----------------------------------------------------------------------

/**
 * ErrorBoundary - Catches JavaScript errors in child component tree
 *
 * Features:
 * - Prevents blank screen crashes
 * - Logs errors to Sentry
 * - Provides user-friendly fallback UI
 * - "Try Again" button to reset and retry
 * - Shows error details in dev mode only
 *
 * Usage:
 *   <ErrorBoundary fallbackTitle="Something went wrong">
 *     <YourComponent />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console in dev, Sentry in prod
    logger.error("ErrorBoundary caught an error", error, {
      componentStack: errorInfo?.componentStack,
      boundary: this.props.name || "unnamed",
    });

    // Capture exception in Sentry with full context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo?.componentStack,
        },
        errorBoundary: {
          name: this.props.name || "unnamed",
        },
      },
      tags: {
        errorBoundary: this.props.name || "unnamed",
      },
    });

    this.setState({ errorInfo });

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const {
      children,
      fallbackTitle = "Something went wrong",
      fallbackDescription = "An unexpected error occurred. Please try again.",
      showRetry = true,
      retryLabel = "Try Again",
      minHeight = 300,
      sx,
    } = this.props;

    if (hasError) {
      // Check if custom fallback component is provided
      if (this.props.fallback) {
        return this.props.fallback({
          error,
          errorInfo,
          resetError: this.handleReset,
        });
      }

      // Default fallback UI
      const isDev = import.meta.env.DEV;

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight,
            p: 3,
            ...sx,
          }}
        >
          <Stack
            alignItems="center"
            spacing={2}
            sx={{
              textAlign: "center",
              maxWidth: 480,
            }}
          >
            <ErrorOutline
              sx={{
                fontSize: 64,
                color: "error.main",
                opacity: 0.8,
              }}
            />

            <Typography variant="h6" color="text.primary">
              {fallbackTitle}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {fallbackDescription}
            </Typography>

            {showRetry && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Refresh />}
                onClick={this.handleReset}
                sx={{ mt: 2 }}
              >
                {retryLabel}
              </Button>
            )}

            {/* Show error details in development only */}
            {isDev && error && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  width: "100%",
                  bgcolor: "error.lighter",
                  borderRadius: 1,
                  textAlign: "left",
                  overflow: "auto",
                  maxHeight: 200,
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    color: "error.dark",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    m: 0,
                  }}
                >
                  {error.toString()}
                  {errorInfo?.componentStack && (
                    <>
                      {"\n\nComponent Stack:"}
                      {errorInfo.componentStack}
                    </>
                  )}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  /** Name identifier for logging/tracking */
  name: PropTypes.string,
  /** Custom title for the fallback UI */
  fallbackTitle: PropTypes.string,
  /** Custom description for the fallback UI */
  fallbackDescription: PropTypes.string,
  /** Whether to show the retry button */
  showRetry: PropTypes.bool,
  /** Label for the retry button */
  retryLabel: PropTypes.string,
  /** Minimum height of the fallback container */
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Custom render function for fallback: ({ error, errorInfo, resetError }) => ReactNode */
  fallback: PropTypes.func,
  /** Callback when an error is caught */
  onError: PropTypes.func,
  /** Callback when reset/retry is triggered */
  onReset: PropTypes.func,
  /** Custom styles */
  sx: PropTypes.object,
};

export default ErrorBoundary;
